import { SelectionModel } from '@angular/cdk/collections';
import { Component, OnInit, Input, Output, ViewChild, AfterViewInit, OnChanges, EventEmitter, SimpleChanges } from '@angular/core';
import { MatPaginator } from '@angular/material/paginator';
import { MatSort } from '@angular/material/sort';
import { MatTableDataSource } from '@angular/material/table';
import { Params } from '@angular/router';
import { GetOrgUserList, MemberUser, SelectorUserData } from '@app/models/user.model';
import { FilterCheck, UserListFilters, UserSelectorData } from '@app/models';
import { appValues, NoProgramFilterValue, PreferredChannels, TOOLTIP_DELAY, USER_OPERATIONS } from '@app/shared';
import { NgForm } from '@angular/forms';
import { paginationHelper } from '@app/shared/data/helpers';

@Component({
    selector: 'app-users-selector-list',
    templateUrl: './users-selector-list.component.html',
    styleUrls: ['./users-selector-list.component.scss'],
    standalone: false
})
export class UsersSelectorListComponent implements OnInit, OnChanges, AfterViewInit {
    @Input('reqProgress') reqProgress: boolean = false;
    @Input('usersList') usersList: GetOrgUserList | undefined = undefined;
    @Input('userSelectorData') userSelectorData!: UserSelectorData;
    @Input('userFilterLists') userFilterLists!: UserListFilters;
    @Input('usersSelectorParams') usersSelectorParams!: Params;
    @Input('broadcastType') broadcastType: string | null = null;
    @Input('initialNote') initialNote!: string;
    @Input('medicaidEnabled') medicaidEnabled: boolean = false;
    @Output('filterList') filterList: EventEmitter<Params> = new EventEmitter<Params>();
    @Output('setUserSelectorData') setUserSelectorData: EventEmitter<MemberUser[] | SelectorUserData[]> = new EventEmitter<MemberUser[] | SelectorUserData[]>();
    tooltipDelay = TOOLTIP_DELAY.DATA_TABLE;
    usersMessages = USER_OPERATIONS;
    displayedColumns: string[] = [
      'select',
      'member_ref', 
      'fname', 
      'lname', 
      'phone_no', 
      'email', 
      'lang', 
      'program_name',
      'channels',
      'active', 
      'org_client_id'
    ];
    dataSource: MatTableDataSource<MemberUser> = new MatTableDataSource();
    selection = new SelectionModel<MemberUser | SelectorUserData>(true, []);
    pageSizeOptions = [20, 50, 100];
    usersLength!: number;
    filterChips: FilterCheck[] = [];

    @ViewChild(MatPaginator, { static: false }) paginator!: MatPaginator;
    @ViewChild(MatSort, { static: false }) sort!: MatSort;
    @ViewChild('filterForm', { static: false }) filterForm!: NgForm;

    searchValue: string = '';
    private readonly _searchAutoComplete: string[] = [];
    noUsers!: boolean;
    noProgramFilterValue = NoProgramFilterValue;

    constructor() {}

    ngOnChanges(changes: SimpleChanges): void {
        this.dataSource.data = this.usersList?.data?.rows || [];
        this.usersLength = this.usersList?.data?.count || 0;
        const hasAnyFilters = (this.usersSelectorParams?.searchText?.length)
          || (this.usersSelectorParams?.gender?.length) 
          || (this.usersSelectorParams?.active?.length) 
          || (this.usersSelectorParams?.language?.length);
        this.noUsers = (!this.usersList) || (this.usersList?.page == 1 && !hasAnyFilters && this.usersLength == 0);
        this.selection = new SelectionModel<MemberUser | SelectorUserData>(true, this.userSelectorData?.confirmedUsers || []);

        this.searchValue = this.usersSelectorParams?.searchText || '';
        if (this.usersSelectorParams?.language || 
            this.usersSelectorParams?.gender || 
            this.usersSelectorParams?.active) {
          this.setFilter(
            this.usersSelectorParams?.language, 
            this.usersSelectorParams?.gender, 
            this.usersSelectorParams?.active);
        } else {
            this.resetFilters();
        }
    }

    ngOnInit(): void {
        // no initial operations required
    }

    /**If the user changes the sort order, reset back to the first page.
     * (make pageIndex to 0 when sort changes)
     * Listen the sort and paginator changes, then filter with current changes in params
     */
    ngAfterViewInit() {
        this.sort.sortChange.subscribe(() => {
            this.paginator.pageIndex = 0;
            this.filterUserList({ order: this.sort.direction, orderBy: this.sort.active });
        });
        this.paginator.page.subscribe(() => {
            this.filterUserList({ limit: this.paginator.pageSize, page: this.paginator.pageIndex + 1 });
        });
    }

    get searchPlaceholder(): string {
        return `Search by Id, First, Last Name, Phone Number${this.medicaidEnabled ? ', Medicaid ID' : ''}`;
    }

    /**Update the autocomplete field
     * invoke the filterUserList() */
    searchList() {
        this.searchValue = this.searchValue?.trim();
        if (this.searchValue !== '' && !this._searchAutoComplete.includes(this.searchValue)) {
            this._searchAutoComplete.push(this.searchValue);
        }
        this.resetFilters();
        let params: { [k: string]: any } = this.searchValue?.length ? { searchText: this.searchValue } : {};
        if (this.searchValue?.length >= 4 && this.medicaidEnabled) {
            params = { ...params, searchTerm: 'local_and_medicaid' };
        }
        this.filterUserList(params, true);
    }

    /**
     * To filter current page with filter params as queryparam
     * @param newParams filters in Params type data
     * @param clearAll to clear the previous filters properties in Params data
     */
    private filterUserList(newParams: { [k: string]: any }, clearAll: boolean = false) {
        if (clearAll) this.usersSelectorParams = {};
        for (let key in newParams) {
            this.usersSelectorParams = { ...this.usersSelectorParams, [key]: newParams[key] };
        }
        if (!this.usersSelectorParams?.order?.length) this.usersSelectorParams['order'] = 'desc';
        if (!this.usersSelectorParams?.orderBy?.length) this.usersSelectorParams['orderBy'] = 'member_ref';
        if (!this.usersSelectorParams?.limit) this.usersSelectorParams['limit'] = 20;
        if (!this.usersSelectorParams?.page) this.usersSelectorParams['page'] = 1;
        this.filterList.emit(this.usersSelectorParams);
    }

    /** The label for the checkbox on the passed row */
    checkboxLabel(row: MemberUser): string {
      return `${this.selection.isSelected(row) ? 'deselect' : 'select'} Ref: ${row.member_ref}`;
    }
    

    /** revert the previous selection from filter for type and Language */
    changeCheckBox(key: 'gender' | 'active' | 'language', value: FilterCheck) {
        if (value.checked) {
            this.userFilterLists[key] = this.userFilterLists[key].map(filter => {
                if (filter.value !== value.value) {
                    return { ...filter, checked: false };
                }
                return filter;
            });
        }
    }
    setFilter(lang: string, gender: string, active: string) {
        this.userFilterLists.language.forEach(f => {
            if (f.value === lang) {
                f.checked = true;
                if (!this.filterChips.includes(f)) this.filterChips.push(f);
            }
        });
        this.userFilterLists.gender.forEach(f => {
            if (f.value === gender) {
                f.checked = true;
                if (!this.filterChips.includes(f)) this.filterChips.push(f);
            }
        });
        this.userFilterLists.active.forEach(f => {
            if (f.value === active) {
                f.checked = true;
                if (!this.filterChips.includes(f)) this.filterChips.push(f);
            }
        });
    }

    /**check the choosen filter
     * remove the Mat-chip
     * invoke filterUserList() with filter operation*/
    removeFilter(value: string) {
        let langIndex = this.userFilterLists.language.findIndex(f => f.value === value);
        if (langIndex >= 0) {
            this.userFilterLists.language[langIndex].checked = false;
        } else {
            let activeIndex = this.userFilterLists.active.findIndex(f => f.value === value);
            if (activeIndex >= 0) {
                this.userFilterLists.active[activeIndex].checked = false;
            } else {
                let genIndex = this.userFilterLists.gender.findIndex(f => f.value === value);
                this.userFilterLists.gender[genIndex].checked = false;
            }
        }
        this.filterChips = this.filterChips.filter(f => f.value !== value);
        this.applyFilters();
    }

    /**Stop propagation to stay the filter menu open
     * resets the filter form
     */
    clearFilter(e: any) {
        e.stopPropagation();
        this.filterForm.reset();
    }

    /**
     * Apply filters in the params
     * invoke fetchBroadcastHistory()
    */
    applyFilters() {
      let languageFilter = this.userFilterLists.language.filter(f => f.checked);
      let genderFilter = this.userFilterLists.gender.filter(f => f.checked);
      let activeFilter = this.userFilterLists.active.filter(f => f.checked);
      
      let newParams :{ [key: string]: any } = {};
      
      if (languageFilter.length) newParams = { ...newParams, language: languageFilter.map(f => f.value).join(',') };
      if (genderFilter.length) newParams = { ...newParams, gender: genderFilter.map(f => f.value).join(',') };
      if (activeFilter.length) newParams = { ...newParams, active: activeFilter.map(f => f.value).join(',') };
      if (this.usersSelectorParams) {
          let { language, gender, active } = this.usersSelectorParams;
          if (language == languageFilter.map(f => f.value).join(',') &&
            gender == genderFilter.map(f => f.value).join(',') &&
            active == activeFilter.map(f => f.value).join(',')) {
            return;
          }
      }

      this.filterChips = [...languageFilter, ...genderFilter, ...activeFilter];
      if (this.searchValue?.length) {
          newParams['searchText'] = this.searchValue;
          if (this.searchValue.length >= 4 && this.medicaidEnabled) {
              newParams['searchTerm'] = 'local_and_medicaid';
          }
      }
      this.filterUserList({ ...newParams }, true);
    }

    checkIfFilters() {
        let language = this.userFilterLists.language.some(f => f.checked);
        let gender = this.userFilterLists.gender.some(f => f.checked);
        let active = this.userFilterLists.active.some(f => f.checked);
        return language || gender || active || this.filterChips.length;
    }


    resetFilters() {
      this.filterChips = [];
      this.userFilterLists.language = this.userFilterLists.language.map(f => ({ ...f, checked: false }));
      this.userFilterLists.gender = this.userFilterLists.gender.map(f => ({ ...f, checked: false }));
      this.userFilterLists.active = this.userFilterLists.active.map(f => ({ ...f, checked: false }));
    }

    toggleSelection(data: MemberUser) {
        if (this.selectedUsers.find(usr => usr.member_ref == data.member_ref)) {
            this.selection = new SelectionModel<MemberUser | SelectorUserData>(
                true, 
                this.userSelectorData.isRadioCheck ? [] : [...this.selectedUsers.filter(usr => usr.member_ref != data.member_ref)]
            );
        } else if (this.userSelectorData.isRadioCheck) {
                this.selection = new SelectionModel<MemberUser | SelectorUserData>(true, [data]);
        } else {
                this.selection.select(data);
        }
        this.setUserSelectorData.emit(this.selection.selected);
    }

    isSelectedRow(row: MemberUser): boolean {
        return !!this.selectedUsers?.find(usr => (usr.member_ref == row.member_ref));
    }

    isDisabled(row: MemberUser): boolean {
        if (this.broadcastType && this.broadcastType === appValues.welcomeMessageParams) {
            if (!row.welcome_sent && !row.active) return false;
            else return true;
        }

        return !row.active;
    }

    getUserChannels(user: any) {
        let deliveryMethod: string[] = [];
        if (user?.client_delivery_method) {
            for (const key in user?.client_delivery_method) {
                if (user?.client_delivery_method?.hasOwnProperty(key) && user?.client_delivery_method[key]) {
                    deliveryMethod.push(PreferredChannels[key]);
                }
            }
        }
        return deliveryMethod;
    }

    getSelectorNote(initialNote: any) {
        if (initialNote?.length) {
            return initialNote;
        }

        return this.broadcastType === appValues.welcomeMessageParams ? 
            this.usersMessages.WELCOME_USER_SELECTOR_NO_USERS : 
            this.usersMessages.USER_SELECTOR_NO_USERS;
    }

    get hasFilters() {
      return this.userFilterLists.language.some(f => f.checked) || 
        this.userFilterLists.gender.some(f => f.checked) ||
        this.userFilterLists.active.some(f => f.checked);
    }

    get searchAutoComplete(): string[] {
        return this._searchAutoComplete.slice(Math.max(this._searchAutoComplete.length - 5, 0));
    }

    get currentPaginateSizes(): number[] {
        return paginationHelper(this.pageSizeOptions, this.usersLength);
    }

    get selectedUsers(): MemberUser[] | SelectorUserData[] {
        return this.selection.selected;
    }

}
